import L from "leaflet";
import { Crosshair, Layers, LocateFixed, Maximize2, MessageCircle, Plus, Route, Sparkles } from "lucide-react";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, Marker, Polyline, TileLayer, Tooltip, useMap, useMapEvents } from "react-leaflet";
import type { ExtractedPlace, ItineraryItemDTO, PlaceDTO } from "@shared/types";
import { boundsOf, bentLine, distanceKm, greatCircle, itemPoints, type LatLng } from "@/lib/geo";
import { CATEGORY, MODE } from "@/lib/meta";
import { useUI, type MapLayer } from "@/lib/store";
import type { Bundle, Derived } from "@/lib/trip";
import { cx, IconButton, Menu, MenuItem } from "../ui";

const ESRI = "https://server.arcgisonline.com/ArcGIS/rest/services";
const ESRI_ATTR = 'Tiles &copy; <a href="https://www.esri.com/">Esri</a>, HERE, Garmin, &copy; OpenStreetMap contributors';
const STADIA_ATTR = '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a> &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';
const TILES: Record<MapLayer, { url: string; attr: string; maxZoom: number; maxNative?: number; overlay?: string }> = {
  light: { url: `${ESRI}/World_Street_Map/MapServer/tile/{z}/{y}/{x}`, attr: ESRI_ATTR, maxZoom: 19 },
  topo: { url: `${ESRI}/World_Topo_Map/MapServer/tile/{z}/{y}/{x}`, attr: ESRI_ATTR, maxZoom: 19 },
  dark: {
    url: `${ESRI}/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}`,
    attr: ESRI_ATTR,
    maxZoom: 19,
    maxNative: 16,
    overlay: `${ESRI}/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}`,
  },
  satellite: {
    url: `${ESRI}/World_Imagery/MapServer/tile/{z}/{y}/{x}`,
    attr: "Tiles &copy; Esri — Maxar, Earthstar Geographics",
    maxZoom: 19,
    overlay: `${ESRI}/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}`,
  },
  osm: { url: "https://tile.openstreetmap.org/{z}/{x}/{y}.png", attr: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>', maxZoom: 19 },
  smooth: { url: "https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png", attr: STADIA_ATTR, maxZoom: 20 },
  smoothDark: { url: "https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png", attr: STADIA_ATTR, maxZoom: 20 },
};

const iconCache = new Map<string, L.DivIcon>();
interface Social {
  ups: number;
  downs: number;
  comments: number;
}

function placeIcon(p: PlaceDTO, o: { color: string; dot: boolean; idea: boolean; dim: boolean; active: boolean; social?: Social | null }) {
  const so = o.social;
  const hot = Boolean(so && (so.ups || so.downs || so.comments));
  const key = `${p.category}|${o.color}|${o.dot}|${o.idea}|${o.dim}|${o.active}|${so ? `${so.ups},${so.downs},${so.comments}` : ""}`;
  let ic = iconCache.get(key);
  if (!ic) {
    const cls = cx(o.dot ? "tb-dot" : "tb-pin", o.idea && "is-idea", o.dim && "is-dim", o.active && "is-active", o.dot && hot && "is-hot");
    let badges = "";
    if (so && !o.dot) {
      if (so.ups || so.downs) {
        const net = so.ups - so.downs;
        badges += `<span class="tb-pin-badge ${net >= 0 ? "is-up" : "is-down"}">${net >= 0 ? "👍" : "👎"}${net >= 0 ? so.ups : so.downs}</span>`;
      }
      if (so.comments) badges += `<span class="tb-pin-cmt">💬${so.comments}</span>`;
    }
    const html = o.dot
      ? `<div class="${cls}" style="--c:${o.color}"></div>`
      : `<div class="${cls}" style="--c:${o.color}">${CATEGORY[p.category]?.emoji ?? "📍"}${badges}</div>`;
    const size = o.dot ? (hot ? 16 : 12) : 34;
    ic = L.divIcon({ html, className: "", iconSize: [size, size], iconAnchor: [size / 2, size / 2], tooltipAnchor: [size / 2 + 2, 0] });
    iconCache.set(key, ic);
  }
  return ic;
}

/** Содержимое подсказки: голоса с именами и последний комментарий */
function SocialTip({ p, d }: { p: PlaceDTO; d: Derived }) {
  const ups = (p.votes ?? []).filter((v) => v.value > 0);
  const downs = (p.votes ?? []).filter((v) => v.value < 0);
  const who = (ids: { userId: string }[]) =>
    ids
      .map((v) => d.members.get(v.userId))
      .filter(Boolean)
      .map((m) => (m!.name ?? m!.email ?? "").split(" ")[0])
      .join(", ");
  const lc = p.lastComment;
  const author = lc ? d.members.get(lc.userId) : null;
  return (
    <div style={{ maxWidth: 240, whiteSpace: "normal" }}>
      <b>{p.name}</b>
      {p.region ? <span style={{ opacity: 0.7 }}> · {p.region}</span> : null}
      {ups.length > 0 && <div>👍 {ups.length} — {who(ups)}</div>}
      {downs.length > 0 && <div>👎 {downs.length} — {who(downs)}</div>}
      {lc && (
        <div style={{ marginTop: 4, fontStyle: "italic" }}>
          💬 <b style={{ fontStyle: "normal" }}>{(author?.name ?? author?.email ?? "").split(" ")[0]}:</b> {lc.body.length > 120 ? `${lc.body.slice(0, 120)}…` : lc.body}
          {(p.commentCount ?? 0) > 1 && <span style={{ opacity: 0.7 }}> (+{(p.commentCount ?? 1) - 1})</span>}
        </div>
      )}
    </div>
  );
}

const candIcon = (e: ExtractedPlace) =>
  L.divIcon({ html: `<div class="tb-pin is-candidate">${CATEGORY[e.category]?.emoji ?? "📍"}</div>`, className: "", iconSize: [34, 34], iconAnchor: [17, 17] });

const modeIcon = (emoji: string, color: string) =>
  L.divIcon({
    html: `<div style="display:grid;place-items:center;width:24px;height:24px;border-radius:999px;background:var(--surface-solid);border:2px solid ${color};font-size:12px;box-shadow:0 2px 6px rgba(0,0,0,.3)">${emoji}</div>`,
    className: "",
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });

interface Segment {
  key: string;
  day: string;
  color: string;
  pts: LatLng[];
  dash?: string;
  weight: number;
  mode?: string;
  item?: ItineraryItemDTO;
  mid?: LatLng;
}

function buildSegments(b: Bundle, d: Derived): Segment[] {
  const segs: Segment[] = [];
  for (const day of d.days) {
    let walk: LatLng[] = [];
    const flushWalk = () => {
      if (walk.length > 1) segs.push({ key: `w-${day.day}-${segs.length}`, day: day.day, color: day.color, pts: walk, dash: "2 7", weight: 2.5 });
      walk = [];
    };
    for (const it of day.items) {
      if (it.kind === "transport") {
        flushWalk();
        const pts = itemPoints(it, d.places);
        if (pts.length === 2) {
          const km = distanceKm(pts[0], pts[1]);
          const line = it.mode === "flight" ? (km > 1500 ? greatCircle(pts[0], pts[1]) : bentLine(pts[0], pts[1])) : pts;
          segs.push({
            key: it.id,
            day: day.day,
            color: day.color,
            pts: line,
            dash: MODE[it.mode ?? "other"]?.dash,
            weight: it.mode === "flight" ? 2.5 : 4,
            mode: it.mode ?? "other",
            item: it,
            mid: line[Math.floor(line.length / 2)],
          });
          walk = [pts[1]];
        }
      } else if (it.kind !== "note") {
        const pts = itemPoints(it, d.places);
        if (pts[0]) walk.push(pts[0]);
      }
    }
    flushWalk();
  }
  void b;
  return segs;
}

/** Leaflet не замечает изменение размера контейнера (панели открываются/закрываются) */
function SizeWatcher() {
  const map = useMap();
  useEffect(() => {
    const el = map.getContainer();
    const ro = new ResizeObserver(() => map.invalidateSize({ debounceMoveend: true }));
    ro.observe(el);
    return () => ro.disconnect();
  }, [map]);
  return null;
}

function ZoomWatcher({ onZoom }: { onZoom: (z: number) => void }) {
  const map = useMapEvents({ zoomend: () => onZoom(map.getZoom()) });
  return null;
}

function ClickHandler({ onPick }: { onPick: (ll: LatLng) => void }) {
  const addMode = useUI((s) => s.addMode);
  useMapEvents({
    click: (e) => {
      if (addMode) onPick([e.latlng.lat, e.latlng.lng]);
    },
    contextmenu: (e) => onPick([e.latlng.lat, e.latlng.lng]),
  });
  return null;
}

function FocusController({ b, d }: { b: Bundle; d: Derived }) {
  const map = useMap();
  const focus = useUI((s) => s.focus);
  const selectedDay = useUI((s) => s.selectedDay);
  const selectedPlaceId = useUI((s) => s.selectedPlaceId);
  const first = useRef(true);

  // стартовый вид — вся поездка в стране назначения (без «хвоста» перелёта из Европы)
  useEffect(() => {
    const pts = b.places.map((p) => [p.lat, p.lng] as LatLng);
    const cc = b.trip.settings.mapCenter;
    const local = cc ? pts.filter((p) => Math.abs(p[0] - cc[0]) < 15 && Math.abs(p[1] - cc[1]) < 15) : pts;
    const bb = boundsOf(local.length ? local : pts);
    if (bb) map.fitBounds(bb, { padding: [30, 30], animate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!focus) return;
    if (focus.bounds) {
      const [a, z] = focus.bounds;
      if (Math.abs(a[0] - z[0]) < 0.002 && Math.abs(a[1] - z[1]) < 0.002) map.flyTo(a, Math.max(map.getZoom(), 14), { duration: 0.8 });
      else map.flyToBounds(focus.bounds, { padding: [60, 60], maxZoom: 15, duration: 0.9 });
    } else if (focus.center) map.flyTo(focus.center, focus.zoom ?? 13, { duration: 0.8 });
  }, [focus, map]);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (!selectedDay) return;
    const info = d.dayIndex.get(selectedDay);
    if (!info) return;
    const pts = info.items.flatMap((it) => itemPoints(it, d.places));
    if (info.night?.placeId) {
      const p = d.places.get(info.night.placeId);
      if (p) pts.push([p.lat, p.lng]);
    }
    // перелёт из Европы не должен растягивать день на полмира
    const local = pts.filter((p) => p[1] > 95 && p[1] < 115 && p[0] > 5 && p[0] < 25);
    const bb = boundsOf(local.length ? local : pts);
    if (bb) map.flyToBounds(bb, { padding: [70, 70], maxZoom: 14, duration: 0.9 });
  }, [selectedDay, d, map]);

  useEffect(() => {
    if (!selectedPlaceId) return;
    const p = d.places.get(selectedPlaceId);
    if (!p) return;
    const ll = L.latLng(p.lat, p.lng);
    if (!map.getBounds().pad(-0.15).contains(ll)) map.flyTo(ll, Math.max(map.getZoom(), 12), { duration: 0.7 });
  }, [selectedPlaceId, d, map]);

  void b;
  return null;
}

const PlacesLayer = memo(function PlacesLayer({ b, d, zoom }: { b: Bundle; d: Derived; zoom: number }) {
  const selectedDay = useUI((s) => s.selectedDay);
  const selectedPlaceId = useUI((s) => s.selectedPlaceId);
  const hoverPlaceId = useUI((s) => s.hoverPlaceId);
  const showIdeas = useUI((s) => s.showIdeas);
  const showSocial = useUI((s) => s.showSocial);
  const selectPlace = useUI((s) => s.selectPlace);
  const dayPlaces = useMemo(() => {
    if (!selectedDay) return null;
    const info = d.dayIndex.get(selectedDay);
    const s = new Set<string>();
    for (const it of info?.items ?? []) if (it.placeId) s.add(it.placeId);
    if (info?.night?.placeId) s.add(info.night.placeId);
    return s;
  }, [selectedDay, d]);

  return (
    <>
      {b.places.map((p) => {
        const days = d.placeDays.get(p.id);
        const inPlan = Boolean(days?.length);
        if (!showIdeas && !inPlan) return null;
        if (p.status === "skipped" && !inPlan && p.id !== selectedPlaceId) return null;
        const di = days?.[0] ? d.dayIndex.get(days[0]) : undefined;
        const color = di?.color ?? CATEGORY[p.category]?.color ?? "#64748b";
        const active = p.id === selectedPlaceId || p.id === hoverPlaceId;
        const dim = Boolean(dayPlaces && !dayPlaces.has(p.id) && !active);
        const dot = zoom < 9 && !active && !(dayPlaces?.has(p.id) && zoom >= 7);
        const showLabel = active || zoom >= 15 || (zoom >= 13 && inPlan && !dayPlaces) || (dayPlaces?.has(p.id) && zoom >= 11);
        return (
          <Marker
            key={p.id}
            position={[p.lat, p.lng]}
            icon={placeIcon(p, {
              color,
              dot,
              idea: !inPlan,
              dim,
              active,
              social: showSocial
                ? { ups: (p.votes ?? []).filter((v) => v.value > 0).length, downs: (p.votes ?? []).filter((v) => v.value < 0).length, comments: p.commentCount ?? 0 }
                : null,
            })}
            zIndexOffset={active ? 1000 : inPlan ? 100 : 0}
            eventHandlers={{
              click: () => selectPlace(p.id),
              mouseover: () => useUI.getState().set({ hoverPlaceId: p.id }),
              mouseout: () => useUI.getState().set({ hoverPlaceId: null }),
            }}
          >
            {showLabel && !(active && showSocial && (p.votes?.length || p.lastComment)) ? (
              <Tooltip key="label" permanent direction="right" className="tb-label" offset={[4, 0]}>
                {p.name}
              </Tooltip>
            ) : (
              <Tooltip key="hover" direction="top" offset={[0, -14]} permanent={active && showSocial && Boolean(p.votes?.length || p.lastComment)}>
                {showSocial ? <SocialTip p={p} d={d} /> : <><b>{p.name}</b>{p.region ? ` · ${p.region}` : ""}</>}
              </Tooltip>
            )}
          </Marker>
        );
      })}
    </>
  );
});

const RouteLayer = memo(function RouteLayer({ segs, zoom }: { segs: Segment[]; zoom: number }) {
  const selectedDay = useUI((s) => s.selectedDay);
  const set = useUI((s) => s.set);
  return (
    <>
      {segs.map((s) => {
        const on = !selectedDay || selectedDay === s.day;
        return (
          <Polyline
            key={s.key + (on ? "-on" : "-off")}
            positions={s.pts}
            pathOptions={{
              color: s.color,
              weight: on ? (selectedDay ? s.weight + 1.5 : s.weight) : Math.max(1.5, s.weight - 1.5),
              opacity: on ? 0.9 : 0.22,
              dashArray: s.dash,
              lineCap: "round",
              lineJoin: "round",
            }}
            eventHandlers={s.item ? { click: () => set({ selectedDay: s.day, selectedItemId: s.item!.id, tab: "plan" }) } : undefined}
          >
            {s.item && (
              <Tooltip sticky>
                {MODE[s.item.mode ?? "other"]?.emoji} {s.item.title}
              </Tooltip>
            )}
          </Polyline>
        );
      })}
      {zoom >= 6 &&
        segs
          .filter((s) => s.mid && s.mode && (!selectedDay || selectedDay === s.day))
          .map((s) => (
            <Marker key={`m-${s.key}`} position={s.mid!} icon={modeIcon(MODE[s.mode as keyof typeof MODE]?.emoji ?? "➡️", s.color)} interactive={false} />
          ))}
    </>
  );
});

function CandidatesLayer({ b, onAdd }: { b: Bundle; onAdd: (sourceId: string, idx: number) => void }) {
  const sid = useUI((s) => s.candidateSourceId);
  const src = b.sources.find((s) => s.id === sid);
  if (!src) return null;
  return (
    <>
      {src.extracted.map((e, i) =>
        e.lat != null && e.lng != null && !e.addedPlaceId ? (
          <Marker key={`c-${i}`} position={[e.lat, e.lng]} icon={candIcon(e)} eventHandlers={{ click: () => onAdd(src.id, i) }}>
            <Tooltip direction="top" offset={[0, -14]}>
              <b>{e.name}</b> — нажмите, чтобы добавить
            </Tooltip>
          </Marker>
        ) : null,
      )}
    </>
  );
}

function MyLocation({ pos }: { pos: LatLng | null }) {
  if (!pos) return null;
  return <Marker position={pos} icon={L.divIcon({ html: '<div class="tb-me"></div>', className: "", iconSize: [16, 16], iconAnchor: [8, 8] })} />;
}

export function TripMap({
  b,
  d,
  onPick,
  onAddCandidate,
  className,
  controlsOffset = 0,
}: {
  b: Bundle;
  d: Derived;
  onPick: (ll: LatLng) => void;
  onAddCandidate: (sourceId: string, idx: number) => void;
  className?: string;
  controlsOffset?: number;
}) {
  const layer = useUI((s) => s.layer);
  const theme = useUI((s) => s.theme);
  const showRoute = useUI((s) => s.showRoute);
  const showIdeas = useUI((s) => s.showIdeas);
  const addMode = useUI((s) => s.addMode);
  const set = useUI((s) => s.set);
  const focusMap = useUI((s) => s.focusMap);
  const [zoom, setZoom] = useState(b.trip.settings.mapZoom ?? 6);
  const [me, setMe] = useState<LatLng | null>(null);
  const segs = useMemo(() => buildSegments(b, d), [b, d]);

  const isDark = theme === "dark" || (theme === "system" && window.matchMedia?.("(prefers-color-scheme: dark)").matches);
  const effective: MapLayer = isDark && layer === "light" ? "dark" : isDark && layer === "smooth" ? "smoothDark" : layer;
  const tiles = TILES[effective];

  const center: LatLng = b.trip.settings.mapCenter ?? [16.2, 106.3];

  const fitAll = () => {
    const pts = b.places.filter((p) => p.lng > 95 && p.lng < 115).map((p) => [p.lat, p.lng] as LatLng);
    const bb = boundsOf(pts.length ? pts : b.places.map((p) => [p.lat, p.lng] as LatLng));
    if (bb) focusMap({ bounds: bb });
    set({ selectedDay: null });
  };

  const locate = () => {
    navigator.geolocation?.getCurrentPosition(
      (p) => {
        const ll: LatLng = [p.coords.latitude, p.coords.longitude];
        setMe(ll);
        focusMap({ center: ll, zoom: 14 });
      },
      () => useUI.getState().toast("Не удалось определить местоположение", { tone: "error" }),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  return (
    <div className={cx("relative h-full w-full", className)}>
      <MapContainer
        center={center}
        zoom={b.trip.settings.mapZoom ?? 6}
        minZoom={2}
        zoomControl={false}
        worldCopyJump
        className={cx("h-full w-full", addMode && "cursor-crosshair")}
        attributionControl
      >
        <TileLayer key={effective} url={tiles.url} attribution={tiles.attr} maxZoom={tiles.maxZoom} maxNativeZoom={tiles.maxNative ?? tiles.maxZoom} />
        {tiles.overlay && <TileLayer key={`${effective}-ov`} url={tiles.overlay} maxZoom={tiles.maxZoom} maxNativeZoom={tiles.maxNative ?? tiles.maxZoom} pane="overlayPane" />}
        <SizeWatcher />
        <ZoomWatcher onZoom={setZoom} />
        <ClickHandler onPick={onPick} />
        <FocusController b={b} d={d} />
        {showRoute && <RouteLayer segs={segs} zoom={zoom} />}
        <PlacesLayer b={b} d={d} zoom={zoom} />
        <CandidatesLayer b={b} onAdd={onAddCandidate} />
        <MyLocation pos={me} />
      </MapContainer>

      {/* правая колонка кнопок */}
      <div className="no-print absolute right-3 z-[500] flex flex-col gap-2" style={{ top: 12 + controlsOffset }}>
        <div className="glass flex flex-col rounded-2xl p-1">
          <Menu
            trigger={(t) => (
              <IconButton label="Слои карты" onClick={t}>
                <Layers size={18} />
              </IconButton>
            )}
          >
            {(close) => (
              <>
                {(
                  [
                    ["light", "Карта"],
                    ["smooth", "Минимальная"],
                    ["topo", "Рельеф"],
                    ["satellite", "Спутник"],
                    ["osm", "OpenStreetMap"],
                    ["dark", "Тёмная"],
                    ["smoothDark", "Тёмная минимальная"],
                  ] as [MapLayer, string][]
                ).map(([k, l]) => (
                  <MenuItem
                    key={k}
                    onClick={() => {
                      set({ layer: k });
                      close();
                    }}
                  >
                    <span className={cx("size-2 rounded-full", layer === k ? "bg-[var(--accent)]" : "bg-[var(--border-strong)]")} />
                    {l}
                  </MenuItem>
                ))}
                <div className="my-1 h-px bg-[var(--border)]" />
                <MenuItem onClick={() => set({ showRoute: !showRoute })}>
                  <Route size={16} /> {showRoute ? "Скрыть маршрут" : "Показать маршрут"}
                </MenuItem>
                <MenuItem onClick={() => set({ showSocial: !useUI.getState().showSocial })}>
                  <MessageCircle size={16} /> {useUI.getState().showSocial ? "Скрыть голоса и комментарии" : "Показать голоса и комментарии"}
                </MenuItem>
                <MenuItem onClick={() => set({ showIdeas: !showIdeas })}>
                  <Sparkles size={16} /> {showIdeas ? "Только места из плана" : "Показать все идеи"}
                </MenuItem>
              </>
            )}
          </Menu>
          <IconButton label="Показать всю поездку" onClick={fitAll}>
            <Maximize2 size={17} />
          </IconButton>
          <IconButton label="Где я" onClick={locate}>
            <LocateFixed size={17} />
          </IconButton>
          <IconButton label={addMode ? "Отменить добавление" : "Добавить точку на карте"} active={addMode} onClick={() => set({ addMode: !addMode })}>
            {addMode ? <Crosshair size={17} /> : <Plus size={18} />}
          </IconButton>
        </div>
      </div>
      {addMode && (
        <div className="glass animate-in absolute top-16 left-1/2 z-[500] -translate-x-1/2 rounded-full px-4 py-2 text-sm font-semibold">
          Нажмите на карту, чтобы поставить место
        </div>
      )}
    </div>
  );
}
